/**
 * VectorGraphic TipTap Node Extension
 * 
 * Custom node for rendering Mermaid diagrams with toggle between
 * rendered view and code editor view.
 */

import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { VectorGraphicComponent } from '../VectorGraphicComponent';

export interface VectorGraphicNodeOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    vectorGraphic: {
      /**
       * Insert a vector graphic (Mermaid diagram)
       */
      insertVectorGraphic: (options: {
        content: string;
        format?: 'mermaid' | 'svg' | 'drawio';
        caption?: string;
        figureNumber?: string;
        diagramId?: string;
        diagramVersion?: string;
        diagramTitle?: string;
      }) => ReturnType;
    };
  }
}

export const VectorGraphicNode = Node.create<VectorGraphicNodeOptions>({
  name: 'vectorGraphic',
  group: 'block',
  atom: true, // Non-editable as a whole unit
  draggable: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      'data-block-id': {
        default: null,
        parseHTML: element => element.getAttribute('data-block-id'),
        renderHTML: attributes => {
          if (!attributes['data-block-id']) return {};
          return { 'data-block-id': attributes['data-block-id'] };
        },
      },
      'data-internal-id': {
        default: null,
        parseHTML: element => element.getAttribute('data-internal-id'),
        renderHTML: attributes => {
          if (!attributes['data-internal-id']) return {};
          return { 'data-internal-id': attributes['data-internal-id'] };
        },
      },
      format: {
        default: 'mermaid',
        parseHTML: element => element.getAttribute('data-format'),
        renderHTML: attributes => ({ 'data-format': attributes.format }),
      },
      content: {
        default: '',
        parseHTML: element => element.getAttribute('data-content'),
        renderHTML: attributes => ({ 'data-content': attributes.content }),
      },
      caption: {
        default: null,
        parseHTML: element => element.getAttribute('data-caption'),
        renderHTML: attributes => {
          if (!attributes.caption) return {};
          return { 'data-caption': attributes.caption };
        },
      },
      figureNumber: {
        default: null,
        parseHTML: element => element.getAttribute('data-figure-number'),
        renderHTML: attributes => {
          if (!attributes.figureNumber) return {};
          return { 'data-figure-number': attributes.figureNumber };
        },
      },
      diagramId: {
        default: null,
        parseHTML: element => element.getAttribute('data-diagram-id'),
        renderHTML: attributes => {
          if (!attributes.diagramId) return {};
          return { 'data-diagram-id': attributes.diagramId };
        },
      },
      diagramVersion: {
        default: null,
        parseHTML: element => element.getAttribute('data-diagram-version'),
        renderHTML: attributes => {
          if (!attributes.diagramVersion) return {};
          return { 'data-diagram-version': attributes.diagramVersion };
        },
      },
      diagramTitle: {
        default: null,
        parseHTML: element => element.getAttribute('data-diagram-title'),
        renderHTML: attributes => {
          if (!attributes.diagramTitle) return {};
          return { 'data-diagram-title': attributes.diagramTitle };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="vector-graphic"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-type': 'vector-graphic',
        class: 'vector-graphic-node',
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(VectorGraphicComponent);
  },

  addCommands() {
    return {
      insertVectorGraphic:
        (options) =>
        ({ commands }) => {
          const blockId = `vg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          return commands.insertContent({
            type: this.name,
            attrs: {
              'data-block-id': blockId,
              'data-internal-id': blockId,
              format: options.format || 'mermaid',
              content: options.content,
              caption: options.caption || null,
              figureNumber: options.figureNumber || null,
              diagramId: options.diagramId || null,
              diagramVersion: options.diagramVersion || null,
              diagramTitle: options.diagramTitle || null,
            },
          });
        },
    };
  },
});
